
















































































import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import DataViewerComponent from "@/components/common/data-viewer/DataViewer.vue";
import { ViewerSection } from "@/components/common/data-viewer/DataViewerModels";
import { CellEditEvent } from "@/components/common/data-viewer/DataViewerModels";
import { mapPdeToViewerSections } from "@/utils/PdeSectionMapper";
import { fetchPdeJson, savePdeJson } from "@/data/repository/PdeRepository";
import Report from "@/data/models/Report";
import {
  fetchBillingCatalog,
  fetchBillingCatalogs,
  fetchBillingItem,
} from "@/data/repository/BillingCatalogRepository";
import {
  BillingCatalogMatch,
  isUsableFirestoreDocumentId,
  resolveBillingCatalogByVersion,
} from "@/utils/BillingCatalogResolver";
import { BillingCatalog } from "@/data/models/BillingCatalog";
import { getBillingCatalogDisplayName } from "@/utils/BillingCatalogDisplay";
import { getBillingCatalogsAccess } from "@/data/repository/ClientRepository";
import { getDownloadUrl } from "@/data/repository/FirebaseImageRepository";
import { editDataViewerAccess } from "@/permissions/UserPermissions";
import { getActiveUser } from "@/data/repository/UserRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { DownloadFileType, getFileTypeIcon } from "@/utils/FileTypeIcons";

/**
 * PDE Viewer component for the Bericht tab.
 * Fetches and displays PDE JSON data using the shared DataViewer.
 */

@Component({
  components: {
    "data-viewer": DataViewerComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class PdeViewer extends Vue {
  /** The PDE report to display */
  @Prop() report!: Report;

  @Ref("dataViewer") dataViewerComponent!: DataViewerComponent;
  @Ref("confirm") confirmationDialogComponent!: ConfirmationDialogComponent;

  /** Whether the viewer has unsaved edits */
  /**
   * Icon of the file this button produces.
   *
   * A PDE is stored as JSON and downloaded as the stored file, so the icon says JSON rather than
   * repeating the button's own label.
   */
  get downloadIcon(): string {
    return getFileTypeIcon(DownloadFileType.JSON);
  }

  get hasUnsavedChanges(): boolean {
    return this.dataViewerComponent?.hasChanges ?? false;
  }

  isLoading = false;
  isSaving = false;
  errorMessage = "";
  downloadUrl = "";
  rawPdeData: Record<string, unknown> | null = null;
  viewerSections: ViewerSection[] = [];

  /** Billing item lookup map: articleId → { headline, text } */
  /**
   * Document id of the catalog this report's PDE names, resolved once per load.
   * Empty when the PDE names none or none matched; `biLookupIssue` says which.
   */
  private biCatalogId = "";

  /** Billing item info dialog state */
  biInfoDialog = false;
  biInfoData = { articleId: "", headline: "", text: "" };

  /**
   * Why LV texts are unavailable or untrustworthy, already translated.
   * Empty when the catalog resolved cleanly. Shown in the billing item dialog so
   * a missing text explains itself instead of failing silently.
   */
  biLookupIssue = "";

  get viewerTitle(): string {
    return this.$t("pdeViewer.title") as string;
  }

  get canEdit(): boolean {
    return editDataViewerAccess(getActiveUser());
  }

  /** Derive download filename from report fields (e.g. "IBTB03457_2604191154_LECKORTUNG_INT.json") */
  get downloadFileName(): string {
    const name = this.report?.fileNameToDownload
      ?? this.report?.fileNameToDisplay
      ?? this.report?.fileName
      ?? this.report?.storageLocation?.split("/").pop()
      ?? "pde";
    return name.endsWith(".json") ? name : `${name}.json`;
  }

  async mounted() {
    await this.loadPdeData();
  }

  @Watch("report")
  async onReportChanged() {
    await this.loadPdeData();
  }

  private async loadPdeData() {
    const storageLocation = this.report?.jsonDataStorageLocation ?? this.report?.storageLocation;
    if (!storageLocation) {
      this.errorMessage = this.$t("pdeViewer.noStorageLocation") as string;
      return;
    }

    this.isLoading = true;
    this.errorMessage = "";

    try {
      // Resolve download URL for the raw JSON file
      if (this.report.storageLocation) {
        try {
          this.downloadUrl = await getDownloadUrl(this.report.storageLocation);
        } catch {
          // Download not available — ignore
        }
      }

      this.rawPdeData = await fetchPdeJson(storageLocation);
      this.viewerSections = mapPdeToViewerSections(this.rawPdeData);

      // Load billing catalog for BI info text lookup (non-blocking)
      this.loadBillingItemLookup();
    } catch {
      // Failure is surfaced to the user via the inline error alert below.
      this.errorMessage = this.$t("pdeViewer.loadError") as string;
    } finally {
      this.isLoading = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCellEdit(_event: CellEditEvent) {
    // Edits are tracked in DataViewer's pendingEdits — no mutation here.
  }

  /**
   * Resolves the catalog named by the PDE's `erpBoQVersion` and remembers its id.
   *
   * The positions themselves are **not** loaded here. The article number is a billing
   * item's document id, so a position is one read on demand rather than a download of
   * the whole catalog — which for Aqua would be 872 documents to show one text.
   *
   * Resolution is document id first, then the legacy `version` match. The id
   * path is what a future PDE contract will use (phase C), so supporting it now
   * means the switch needs no further change here. Failures are recorded in
   * `biLookupIssue` rather than returning silently, because a missing or wrong
   * LV text is otherwise indistinguishable from a position that genuinely has
   * no text.
   */
  private async loadBillingItemLookup() {
    // Reset first: @Watch("report") reloads on the same component instance, so
    // anything left here would be the previous report's catalog resolved against this
    // report's positions.
    this.biCatalogId = "";
    this.biLookupIssue = "";
    if (!getBillingCatalogsAccess() || !this.rawPdeData) return;

    const projectInfo = (this.rawPdeData["0_projectInfo"] as Record<string, unknown>[])?.[0];
    const erpBoQVersion = projectInfo?.erpBoQVersion as string | null;
    if (!erpBoQVersion) {
      this.biLookupIssue = this.$t("pdeViewer.biInfo.noCatalogReference") as string;
      return;
    }

    try {
      const catalog = await this.resolveBillingCatalog(erpBoQVersion);
      if (catalog) this.biCatalogId = catalog.id;
    } catch {
      // Non-critical: the viewer still works without LV texts, but say so.
      this.biLookupIssue = this.$t("pdeViewer.biInfo.catalogLoadFailed") as string;
    }
  }

  /**
   * Resolves the PDE's `erpBoQVersion` to a billing catalog, recording why in
   * `biLookupIssue` when the answer is not a single clean match.
   *
   * @returns The catalog to read item texts from, or null when none matched.
   */
  private async resolveBillingCatalog(erpBoQVersion: string): Promise<BillingCatalog | null> {
    if (isUsableFirestoreDocumentId(erpBoQVersion)) {
      const byId = await fetchBillingCatalog(erpBoQVersion);
      if (byId && byId.status === "active") return byId;
    }

    const catalogs = await fetchBillingCatalogs();
    const resolution = resolveBillingCatalogByVersion(catalogs, erpBoQVersion);

    if (resolution.match === BillingCatalogMatch.NOT_FOUND) {
      this.biLookupIssue = this.$t("pdeViewer.biInfo.catalogNotFound", [erpBoQVersion]) as string;
      return null;
    }

    if (resolution.match === BillingCatalogMatch.AMBIGUOUS && resolution.catalog) {
      // A plausible-but-wrong match is worse than none, so this one does not
      // wait for the user to open a position.
      this.biLookupIssue = this.$t("pdeViewer.biInfo.catalogAmbiguous", [
        String(resolution.matchCount),
        erpBoQVersion,
        getBillingCatalogDisplayName(resolution.catalog),
      ]) as string;
      SnackbarModule.showError({
        key: "pdeViewer.biInfo.catalogAmbiguous",
        params: [String(resolution.matchCount), erpBoQVersion, getBillingCatalogDisplayName(resolution.catalog)],
      });
    }

    return resolution.catalog;
  }

  /**
   * Handles clicking a billingItemId cell — shows the LV text for that position.
   *
   * One document read, keyed by the article number exactly as the PDE carries it,
   * suffix included. A position the catalog does not hold leaves the dialog showing
   * `biInfo.notFound`, which is the honest answer rather than a blank.
   */
  async onCellClick(event: { sectionId: string; rowIndex: number; columnKey: string; value: string | number | null }) {
    if (event.columnKey !== "billingItemId" || !event.value) return;

    const articleId = String(event.value);
    let biInfo: { headline: string; text: string } | null = null;

    if (this.biCatalogId !== "") {
      try {
        const item = await fetchBillingItem(this.biCatalogId, articleId);
        if (item) biInfo = { headline: item.headline ?? "", text: item.text ?? "" };
      } catch {
        this.biLookupIssue = this.$t("pdeViewer.biInfo.catalogLoadFailed") as string;
      }
    }

    this.biInfoData = {
      articleId,
      headline: biInfo?.headline ?? "",
      text: biInfo?.text ?? "",
    };
    this.biInfoDialog = true;
  }

  /** Check for unsaved changes, then close if confirmed. */
  async onClose() {
    if (await this.confirmDiscardIfNeeded()) {
      this.$emit("close");
    }
  }

  /**
   * Checks for unsaved changes and shows a discard dialog if needed.
   * Returns true if safe to proceed (no changes or user confirmed discard).
   */
  async confirmDiscardIfNeeded(): Promise<boolean> {
    if (!this.hasUnsavedChanges) return true;
    return await this.confirmationDialogComponent.show({
      title: this.$t("common.confirmationDialog.cancelTitle").toString(),
      message: this.$t("common.confirmationDialog.cancelMessage").toString(),
    });
  }

  /** Revert displayed data to the original fetched state */
  onDiscard() {
    if (this.rawPdeData) {
      this.viewerSections = mapPdeToViewerSections(this.rawPdeData);
    }
  }

  /** Force-download the PDE JSON file instead of opening in browser */
  async onDownload() {
    if (!this.downloadUrl) return;
    try {
      const response = await fetch(this.downloadUrl);
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = this.downloadFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch {
      SnackbarModule.showError({ key: "pdeViewer.loadError" });
    }
  }

  /**
   * Saves the edited PDE back to Cloud Storage, replacing the existing file
   * at the same path. Edits are applied to a deep copy of the originally
   * fetched JSON (not reconstructed from the viewer rows) so that field
   * ordering, unedited sections, and unmapped fields are preserved.
   */
  async onSave(edits: CellEditEvent[]) {
    if (!this.rawPdeData || edits.length === 0) return;

    const storageLocation = this.report?.jsonDataStorageLocation ?? this.report?.storageLocation;
    if (!storageLocation) {
      SnackbarModule.showError({ key: "pdeViewer.noStorageLocation" });
      return;
    }

    const updatedPde = this.applyEdits(this.rawPdeData, edits);

    this.isSaving = true;
    try {
      await savePdeJson(storageLocation, updatedPde);
      // Persist the saved version locally so further edits build on it.
      this.rawPdeData = updatedPde;
      this.dataViewerComponent?.clearPendingEdits();
      SnackbarModule.showSuccess({ key: "pdeViewer.saveSuccess" });
    } catch {
      // Keep pending edits so the user can retry after a failed save.
      SnackbarModule.showError({ key: "pdeViewer.saveError" });
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Returns a deep clone of the PDE document with the given cell edits applied.
   * A section's viewer `id` equals its top-level PDE key and `rowIndex` indexes
   * the raw section array in order, so each edit maps directly onto
   * `clone[sectionId][rowIndex][columnKey]`. When the original field value was
   * numeric, the (string) input value is coerced back to a number to preserve
   * the document's original types.
   */
  private applyEdits(
    source: Record<string, unknown>,
    edits: CellEditEvent[],
  ): Record<string, unknown> {
    const clone = JSON.parse(JSON.stringify(source)) as Record<string, unknown>;
    for (const edit of edits) {
      const section = clone[edit.sectionId];
      if (!Array.isArray(section)) continue;
      const row = section[edit.rowIndex];
      if (!row || typeof row !== "object") continue;

      let value = edit.newValue;
      const original = (row as Record<string, unknown>)[edit.columnKey];
      if (typeof original === "number" && typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) value = parsed;
      }
      (row as Record<string, unknown>)[edit.columnKey] = value;
    }
    return clone;
  }
}
